import { Grid, GridColumn, GridRow, Input } from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import type { CombinedProfile } from '../../pages/Profile/ProfilePage';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { ModalBottomButtons } from '../ModalBottomButtons/ModalBottomButtons';
import styles from './ProfileForm.module.scss';
import { schema } from './validation/schema';

const UpdateBpProfileMutationDocument = graphql(`
  mutation UpdateBpProfileInProfileForm($update: BpProfileUpdateInput!, $uuid: String!) {
    updateBpProfile(update: $update, uuid: $uuid) {
      uuid
      __typename
      email
      threemaId
      firstName
      lastName
    }
  }
`);

export const ProfileFormFieldsDocument = graphql(`
  fragment ProfileFormFields on Query {
    profiles(where: $profilesWhere, options: $profilesOptions) {
      uuid
      __typename
      email
      threemaId
      firstName
      lastName
    }
  }
`);

type ProfileFormType = CombinedProfile & {
  currentPassword: string;
  newPassword: string;
  newPasswordCopy: string;
};

type ProfileFormProps = {
  query: FragmentType<typeof ProfileFormFieldsDocument> | undefined;
  onClose: () => void;
};

export const ProfileForm = ({ query, onClose }: ProfileFormProps) => {
  const fragment = getFragmentData(ProfileFormFieldsDocument, query);
  const profile = fragment?.profiles[0];
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const { threemaLicense, threemaBroadcastLicense } = useContext(OrganizationConfigContext);

  const [, updateProfile] = useMutation(UpdateBpProfileMutationDocument);

  const formikValues: ProfileFormType = {
    email: profile?.email ?? '',
    loginEmail: pimAuthClaims.getUser().email,
    threemaId: profile?.threemaId ?? '',
    firstName: profile?.firstName ?? '',
    lastName: profile?.lastName ?? '',
    currentPassword: '',
    newPassword: '',
    newPasswordCopy: '',
  };

  const handleSubmit = async (values: ProfileFormType) => {
    let closeModal: boolean = false;

    if (values.email !== formikValues.email) closeModal = await handleEmailUpdate(values);
    if (values.threemaId !== formikValues.threemaId) closeModal = await handleThreemaUpdate(values);
    if (closeModal && onClose) onClose();
  };

  const handleEmailUpdate = async (values: ProfileFormType) => {
    if (typeof values.email === 'string') {
      const { error } = await updateProfile({
        update: {
          email: values.email,
        },
        uuid: pimAuthClaims.getProfile().uuid,
      });

      error ? showErrorToast(error) : showSuccessToast(t('profiles.emailSaved'));
    }
    return true;
  };

  const handleThreemaUpdate = async (_values: ProfileFormType) => {
    return true;
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={formikValues}
      validationSchema={schema}
      initialErrors={{ some: 'error' }} //  deactivates the submit button
    >
      {({ resetForm, isSubmitting, errors, handleChange, values }) => {
        return (
          <Form className={styles['profile-form']}>
            <Grid>
              <GridRow mobileGap='var(--grid-column-gap)'>
                <GridColumn width={6}>
                  <Input
                    label={t('common.notificationEmail')}
                    onChange={handleChange}
                    name={'email'}
                    value={values.email}
                    type={'text'}
                    error={errors.email}
                  />

                  <Input
                    label={t('profiles.threemaId')}
                    onChange={handleChange}
                    name={'threemaId'}
                    value={values.threemaId ?? ''}
                    type={'text'}
                    error={errors.threemaId}
                    disabled={!threemaLicense && !threemaBroadcastLicense}
                  />
                </GridColumn>
              </GridRow>
            </Grid>

            <ModalBottomButtons
              isLoading={isSubmitting}
              closeButton={{
                callback: () => {
                  resetForm();
                  if (onClose) onClose();
                },
              }}
              errors={errors}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
